import { runDetached } from '@hprofits/utils';

import {
  clearAuthTokens,
  readRefreshTokenRaw,
  readTokenRaw,
  readUser,
  saveRefreshTokenRaw,
  saveTokenRaw,
} from './authStorage';
import { decodeExp } from './decodeExp';
import { isImpersonating, saveImpTokens } from './tabStorage';

// Single-instance auth state machine shared by legacy $api (axios) and the stateful api-client (fetch).
// Refresh is serialized per-tab via this.pending and cross-tab via Web Locks; refresh fires reactively
// (on 401) or just-in-time from the request interceptor. Cross-tab sync uses `storage` events.

export type AuthState = 'anon' | 'authed' | 'refreshing' | 'dead';
export type Tokens = { accessToken: string; refreshToken: string };
export type RefreshFn = () => Promise<Tokens>;
export type RefreshFailedListener = (err: unknown) => void;

const HTTP_BAD_REQUEST = 400;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
const TERMINAL_REFRESH_STATUSES: ReadonlySet<number> = new Set([HTTP_BAD_REQUEST, HTTP_UNAUTHORIZED, HTTP_FORBIDDEN]);

const FIRST_RETRY_DELAY_MS = 1_000;
const SECOND_RETRY_DELAY_MS = 3_000;
const REFRESH_RETRY_DELAYS_MS: ReadonlyArray<number> = [FIRST_RETRY_DELAY_MS, SECOND_RETRY_DELAY_MS];
const PROACTIVE_REFRESH_BUDGET_MS = 30_000;
const MS_PER_SECOND = 1_000;

const readStatus = (err: unknown): number | undefined => {
  if (!err || typeof err !== 'object') {
    return undefined;
  }
  if ('status' in err && typeof err.status === 'number') {
    return err.status;
  }
  if (
    'response' in err &&
    err.response &&
    typeof err.response === 'object' &&
    'status' in err.response &&
    typeof err.response.status === 'number'
  ) {
    return err.response.status;
  }
  return undefined;
};

const isTerminalRefreshError = (err: unknown): boolean => {
  const status = readStatus(err);
  return status !== undefined && TERMINAL_REFRESH_STATUSES.has(status);
};

const wait = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

// Cross-tab refresh mutex: only one tab in the origin runs the network call at a time, others queue.
// Falls back to no serialization on old browsers without Web Locks (in-tab `pending` still dedups).
const REFRESH_LOCK_NAME = '@hprofits/auth-refresh';
const withRefreshLock = <T>(fn: () => Promise<T>): Promise<T> => {
  if (typeof navigator !== 'undefined' && navigator.locks?.request) {
    // Cast: lib.dom typing infers Promise<T> as result T even though runtime auto-unwraps.
    return navigator.locks.request(REFRESH_LOCK_NAME, () => fn()) as Promise<T>;
  }
  return fn();
};

export class AuthSession {
  private state: AuthState = 'anon';
  private listeners = new Set<(s: AuthState) => void>();
  private refreshFailedListeners = new Set<RefreshFailedListener>();
  private refreshFn: RefreshFn;
  private pending: Promise<Tokens> | null = null;
  private channel: BroadcastChannel | null = null;

  constructor(config: { refreshFn: RefreshFn }) {
    this.refreshFn = config.refreshFn;
    if (readTokenRaw() && readRefreshTokenRaw()) {
      this.state = 'authed';
    }
  }

  getState(): AuthState {
    return this.state;
  }

  getAccessToken(): string | null {
    return readTokenRaw();
  }

  getRefreshToken(): string | null {
    return readRefreshTokenRaw();
  }

  isAccessTokenExpiring(budgetMs: number): boolean {
    const token = readTokenRaw();
    if (!token) {
      return false;
    }
    const exp = decodeExp(token);
    return exp !== null && exp * MS_PER_SECOND - Date.now() < budgetMs;
  }

  // Requires current-user in storage before being called - login flow writes it first.
  // Imp tabs don't: their tokens live in sessionStorage, and an admin signing out in another tab
  // removes current-user, which used to drop the imp tab's refreshed tokens (#675).
  setTokens(tokens: Tokens): void {
    if (isImpersonating()) {
      saveImpTokens(tokens);
      this.transition('authed');
      return;
    }
    const email = readUser();
    if (!email) {
      throw new Error('AuthSession.setTokens: no current-user in storage');
    }
    saveTokenRaw(tokens.accessToken, email);
    saveRefreshTokenRaw(tokens.refreshToken, email);
    this.transition('authed');
  }

  // Idempotent. Leaves localStorage['role'] alone so a subsequent re-login restores it via entryRole.
  logout(): void {
    this.channel?.postMessage({ type: 'logout' });
    clearAuthTokens();
    this.transition('dead');
  }

  async refresh(): Promise<Tokens> {
    if (this.state === 'dead') {
      throw new Error('AuthSession: auth is dead');
    }
    if (this.pending) {
      return this.pending;
    }

    this.transition('refreshing');
    const accessAtStart = readTokenRaw();
    this.pending = withRefreshLock<Tokens>(async () => {
      // Another tab refreshed while we waited for the lock - adopt its result, skip the call.
      const currentAccess = readTokenRaw();
      const currentRefresh = readRefreshTokenRaw();
      if (currentAccess && currentRefresh && currentAccess !== accessAtStart) {
        this.transition('authed');
        return { accessToken: currentAccess, refreshToken: currentRefresh };
      }
      try {
        const tokens = await this.refreshWithRetries(REFRESH_RETRY_DELAYS_MS);
        this.setTokens(tokens);
        return tokens;
      } catch (err) {
        // Non-auth errors (network) keep tokens so a later retry can succeed.
        if (isTerminalRefreshError(err)) {
          this.logout();
        } else {
          this.transition('authed');
          this.refreshFailedListeners.forEach((cb) => cb(err));
        }
        throw err;
      }
    }).finally(() => {
      this.pending = null;
    });

    return this.pending;
  }

  onChange(cb: (s: AuthState) => void): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  onRefreshFailed(cb: RefreshFailedListener): () => void {
    this.refreshFailedListeners.add(cb);
    return () => this.refreshFailedListeners.delete(cb);
  }

  // BroadcastChannel is a belt-and-suspenders logout signal; `storage` events cover the common path.
  initialize(): void {
    window.addEventListener('storage', this.onStorageEvent);
    document.addEventListener('visibilitychange', this.onResume);
    window.addEventListener('online', this.onResume);
    if (typeof BroadcastChannel !== 'undefined') {
      this.channel = new BroadcastChannel('auth-session');
      this.channel.onmessage = (e: MessageEvent<{ type: string }>) => {
        if (e.data?.type !== 'logout') {
          return;
        }
        // Stale signal from a tab that lost a refresh race; if storage still has tokens, ignore.
        if (readTokenRaw() && readRefreshTokenRaw()) {
          return;
        }
        this.transition('dead');
      };
    }
  }

  teardown(): void {
    window.removeEventListener('storage', this.onStorageEvent);
    document.removeEventListener('visibilitychange', this.onResume);
    window.removeEventListener('online', this.onResume);
    this.channel?.close();
    this.channel = null;
  }

  // Imp tabs ignore admin-storage churn - their session lives on sessionStorage.
  private onStorageEvent = (e: StorageEvent): void => {
    if (e.key !== 'jwt-tokens' && e.key !== 'jwt-refresh-tokens' && e.key !== 'current-user') {
      return;
    }
    if (isImpersonating()) {
      return;
    }
    const access = readTokenRaw();
    const refresh = readRefreshTokenRaw();
    if (!access || !refresh) {
      this.transition('dead');
      return;
    }
    this.transition('authed');
  };

  private onResume = (): void => {
    if (document.visibilityState !== 'visible' || this.state !== 'authed') {
      return;
    }
    if (!this.isAccessTokenExpiring(PROACTIVE_REFRESH_BUDGET_MS)) {
      return;
    }
    runDetached(this.refresh());
  };

  private async refreshWithRetries(delaysMs: ReadonlyArray<number>): Promise<Tokens> {
    const [delayMs, ...restDelaysMs] = delaysMs;
    try {
      return await this.refreshFn();
    } catch (err) {
      if (delayMs === undefined || isTerminalRefreshError(err)) {
        throw err;
      }
      await wait(delayMs);
      return this.refreshWithRetries(restDelaysMs);
    }
  }

  private transition(next: AuthState): void {
    if (this.state === next) {
      return;
    }
    this.state = next;
    this.listeners.forEach((cb) => cb(next));
  }
}
